/* eslint-disable max-depth, complexity, max-statements */

import type { Widget_UWA } from '@ds/swymkit/typings'

import {
  type ContentReaderConfiguration,
  ContentReaderMode,
} from '~/features-swym/widgets/content-reader/interfaces/content-reader'

import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { getIdFromSubjectUri } from '~/features-shared/libs/utils/subject-uri-utils'

const exaleadContentTypes = {
  'post': 'posts',
  'media': 'media',
  'question': 'iquestion',
  'idea': 'idea',
  'wiki': 'wikitree',
  'survey': 'survey',
  'wedo': 'wedo',
  'social-event': 'conference',
}

export function extractTypeAndIdArray(multiContentIds: string) {
  const contentTypesRegExp = 'post|media|question|idea|wiki|survey|wedo|social-event'
  const derivedTypesRegExp = '(?:dt-post-|dt-idea-)[A-Za-z0-9-_%.:]+'
  const typeRegExp = `${contentTypesRegExp}|${derivedTypesRegExp}`
  // CRA Verify
  const re = new RegExp(
    // eslint-disable-next-line regexp/no-dupe-characters-character-class
    `(${typeRegExp}):((?!(${typeRegExp}))[0-9a-zA-Z-_:]+|,)+`,
    'gi',
  )
  const list = multiContentIds.match(re) as RegExpMatchArray
  const contentIdArray = []
  const contentTypeArray = []
  for (const item of list) {
    // check if a type is specified
    if (item.indexOf(':') > 0) {
      const tmp = item.split(/:(.+)/)
      const type = tmp[0].toLowerCase()
      const ids = tmp[1].split(',') // ids will be of the form ['1', '2', '3', '']
      for (let i = 0, len = ids.length; i < len; i++) {
        const id = ids[i]
        if (id.length > 0) {
        // the id can now be a digit (v1)
        // or a string base64 (v2, 0-9a-Z-_)
          // eslint-disable-next-line regexp/prefer-w, regexp/use-ignore-case, regexp/no-useless-flag
          if (!(/[^0-9a-zA-Z-_:]/g).test(id))
            contentIdArray.push(id)
          contentTypeArray.push(type)
        }
      }
    }
  }
  return { contentIdArray, contentTypeArray }
}

export function createExaleadQueryParameters(typeArr: string[], idArr: string[]) {
  const postArr: string[] = [] // contains all the post ids of the elements specified in the preferences
  const mediaArr: string[] = [] // contains all the media ids of the elements specified in the preferences
  const iqArr: string[] = [] // contains all the question ids of the elements specified in the preferences
  const ideaArr: string[] = [] // contains all the idea ids of the elements specified in the preferences
  const wikiArr: string[] = [] // contains all the wiki ids of the elements specified in the preferences
  const surveyArr: string[] = [] // contains all the survey ids of the elements specified in the preferences
  const wedoArr: string[] = [] // contains all the wedo ids of the elements specified in the preferences
  const socialEventArr: string[] = [] // contains all the social-event ids of the elements specified in the preferences

  const getIdQuery = (id: string) => `id:"${id}"`

  const queryArr = []
  typeArr.forEach((type, index) => {
    if (type === IContentModel.Post)
      postArr.push(getIdQuery(idArr[index]))
    else if (type === IContentModel.Idea)
      ideaArr.push(getIdQuery(idArr[index]))
    else if (type === IContentModel.Media)
      mediaArr.push(getIdQuery(idArr[index]))
    else if (type === IContentModel.Question)
      iqArr.push(getIdQuery(idArr[index]))
    else if (type === IContentModel.Survey)
      surveyArr.push(getIdQuery(idArr[index]))
    else if (type === IContentModel.Wedo)
      wedoArr.push(getIdQuery(idArr[index]))
    else if (type === IContentModel.Wiki)
      wikiArr.push(getIdQuery(idArr[index]))
    else if (type === 'social-event' || type === 'conference')
      socialEventArr.push(getIdQuery(getIdFromSubjectUri(idArr[index])))
  })

  // populate the query parameters to be used for the Exalead call
  if (typeArr.length && idArr.length) {
    if (postArr.length)
      queryArr.push(`(type:${exaleadContentTypes.post} AND (${postArr.join(' OR ')}))`)

    if (mediaArr.length)
      queryArr.push(`(type:${exaleadContentTypes.media} AND (${mediaArr.join(' OR ')}))`)

    if (iqArr.length)
      queryArr.push(`(type:${exaleadContentTypes.question} AND (${iqArr.join(' OR ')}))`)

    if (ideaArr.length)
      queryArr.push(`(type:${exaleadContentTypes.idea} AND (${ideaArr.join(' OR ')}))`)

    if (wikiArr.length)
      queryArr.push(`(type:${exaleadContentTypes.wiki} AND (${wikiArr.join(' OR ')}))`)

    if (surveyArr.length)
      queryArr.push(`(type:${exaleadContentTypes.survey} AND (${surveyArr.join(' OR ')}))`)

    if (wedoArr.length)
      queryArr.push(`(type:${exaleadContentTypes.wedo} AND (${wedoArr.join(' OR ')}))`)

    if (socialEventArr.length)
      queryArr.push(`(type:${exaleadContentTypes['social-event']} AND (${socialEventArr.join(' OR ')}))`)
  }

  return queryArr.join(' OR ')
}

export function processContentReaderWidgetData(widget: Widget_UWA): ContentReaderConfiguration {
  const contentId = widget.getValue('contentId') as string
  const contentType = widget.getValue('contentType') as string
  const multiContentIds = (widget.getValue('multiContentIds') as string).trim()
  let contentIdArray: string[] = []
  let contentTypeArray: string[] = []
  const readerMode = contentType === 'multi'
    ? ContentReaderMode.MULTI_CONTENT
    : (contentId.length ? ContentReaderMode.SINGLE_CONTENT : ContentReaderMode.NO_CONTENT)
  let refineUsingQuery = ''
  if (readerMode === 'multi-content') {
    ({ contentIdArray, contentTypeArray } = extractTypeAndIdArray(multiContentIds))
    refineUsingQuery = createExaleadQueryParameters(contentTypeArray, contentIdArray)
  }
  else if (readerMode === ContentReaderMode.SINGLE_CONTENT) {
    contentIdArray = [contentId]
    contentTypeArray = [contentType]
  }
  return {
    readerMode,
    contentIdArray,
    contentTypeArray,
    refineUsingQuery,
    widgetId: widget.id,
    isReadOnly: widget.readOnly,
  }
}
